import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import isEqual from 'deep-equal';

import { setInitialBannerFilterValues } from 'shared/banners/bannerParams';
import { trackResultPage } from '@/lib/tracking/tracking';
import * as resultActions from '@/ducks/results/resultsActions';

import { Autocomplete } from '@/components/autocomplete/Autocomplete';
import { TopBar } from './components/topBar/TopBar';
import { MobileTopBar } from './components/topBar/MobileTopBar';
import { ResultList } from './components/resultList/ResultList';
import { ResultFilter } from './components/resultFilter/ResultFilter';
import { Sorting } from './components/sorting/Sorting';
import { SmallFhhMap } from './components/smallFhhMap/SmallFhhMap';
import { FhhSearch } from './components/newSearch/FhhSearch';
import type { DeviceType, FhhState, Filters, ResultData } from '@/domain';
import { queryParamToString } from 'shared/util/queryParamConverters';
import { queryString } from 'shared/util/query';
import { useSearchResultsWithPricesRegular } from '@/hooks/useSearchResultsWithPricesRegular';

export interface WithPricesResultPageProps {
    deviceType: DeviceType;
    loginId: number;
    searchId: string;
}

export const WithPricesResultPage = ({ deviceType, loginId, searchId }: WithPricesResultPageProps) => {
    const router = useRouter();
    const dispatch = useDispatch();

    const { filters, filterData, autocompleteData, showFiltersOverlay } = useSelector((state: FhhState) => state.results);

    const isMobile = deviceType.isProbablyMobile;

    const { data, loading } = useSearchResultsWithPricesRegular(searchId, filters);

    useEffect(() => {
        if (!isFalsyOrEmpty(router?.query)) {
            dispatch(resultActions.updateFilters({ ...router.query, nrFUSAds: deviceType.isProbablyMobile ? 1 : 2 }));
        }

        setInitialBannerFilterValues({
            pageType: 'result',
            page: filters.page ?? undefined,
            destination: {
                city: autocompleteData?.trackingInfo?.city || filters.city || queryParamToString(router?.query?.city),
                country: autocompleteData?.trackingInfo?.country || filters.country || queryParamToString(router?.query?.country),
            },
        });
    }, []);

    const prevFilters = useRef<Filters>(filters);
    const prevData = useRef<ResultData>();
    useEffect(() => {
        const unchangedFilters = isEqual(filters, prevFilters.current);

        if (unchangedFilters && isEqual(data, prevData.current)) {
            return;
        }
        prevFilters.current = filters;
        prevData.current = data;
        // On a page load, the stats object is empty. This object is needed for tracking, so must wait until
        // next update when stats is set before tracking. After that, track on every filter change.
        if (data.stats?.numResults !== undefined) {
            trackResultPage('Grid', { data, filters });
        }

        if (unchangedFilters) {
            return;
        }
        router?.replace({ pathname: router.pathname, query: filters }, undefined, { shallow: true });
    }, [queryString(filters), data.stats?.numResults]);

    const isFalsyOrEmpty = (value) =>
        !value || (Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}');

    const filterResults = (filter: Filters) => dispatch(resultActions.filterResults(filter));
    const fetchAutoComplete = (query, sessionToken) => dispatch(resultActions.fetchAutocomplete(query, sessionToken));
    const updateAutocomplete = (selected, trackingInfo = null) => dispatch(resultActions.updateAutocomplete(selected, trackingInfo));

    return (
        <div data-testid="resultPage">
            <MobileTopBar
                stats={data.stats}
                autocompleteData={autocompleteData}
                filters={filters}
                hideFilters={() => dispatch(resultActions.hideFilters())}
                showFilters={() => dispatch(resultActions.showFilters())}
                showFiltersOverlay={Boolean(showFiltersOverlay)}
                isLoggedIn={loginId != null}
            />
            <TopBar stats={data.stats} onFilterChanged={filterResults} filters={filters} />
            <FhhSearch filters={filters} />
            <section className="grid md:grid-cols-3 gap-32">
                <div className={showFiltersOverlay ? undefined : 'hidden md:block'} data-testid="Sidebar-desktop">
                    <ResultFilter
                        autocompleteData={autocompleteData}
                        fetchAutocomplete={fetchAutoComplete}
                        updateAutocomplete={updateAutocomplete}
                        filters={filters}
                        filterData={filterData}
                        onFilterChanged={filterResults}
                        isLoggedIn={loginId != null}
                        isMobile={isMobile}
                    />
                </div>
                <div className={`md:col-span-2${showFiltersOverlay ? ' hidden md:block' : ''}`}>
                    <div className="md:hidden" data-testid="geoAutocomplete">
                        <Autocomplete
                            id={'destination-autocomplete'}
                            data={autocompleteData}
                            onFilterChanged={filterResults}
                            fetch={fetchAutoComplete}
                            updateAutocomplete={updateAutocomplete}
                            filters={filters}
                        />
                        <SmallFhhMap filters={filters} />
                        <Sorting filters={filters} onFilterChanged={filterResults} />
                    </div>
                    <ResultList
                        results={data.resultItems}
                        stats={data.stats}
                        filters={filters}
                        onFilterChanged={filterResults}
                        numberOfPreloadedImages={6}
                        isMobile={isMobile}
                        loginId={loginId}
                        loading={loading}
                    />
                </div>
            </section>
        </div>
    );
};
